import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import {
	type EditorState,
	NodeSelection,
	PluginKey,
	TextSelection,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { CellSelection } from '@atlaskit/editor-tables';
import { fg } from '@atlaskit/platform-feature-flags';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';

import {
	createRemixThinkingWidgetDecoration,
	getRemixThinkingWidgetContainer,
	renderRemixThinkingPortal,
	THINKING_WIDGET_KEY,
} from './remix-thinking-decoration';

const THINKING_HIGHLIGHT_CLASS = 'remix-thinking-highlight';

export type SavedSelection = {
	anchorCellPos?: number;
	from: number;
	headCellPos?: number;
	isNodeSelection: boolean;
	to: number;
};

// types in this interface use | undefined to make sure we don't miss the field when updating the plugin state
export type RemixThinkingPluginState = {
	contentType?: string;
	decorationSet: DecorationSet;
	dynamicUiSubtypeForUpdate: string | undefined;
	historyEntryForUpdate: RemixHistoryEntryForUpdate | undefined;
	isComplete: boolean | undefined;
	isThinking: boolean;
	isUpdate: boolean | undefined;
	savedSelection?: SavedSelection;
};

export type RemixThinkingPluginMeta = {
	anchorCellPos?: number;
	cellSelectionRanges?: Array<{ from: number; to: number }>;
	clearDecoration?: boolean;
	contentType?: string;
	dynamicUiSubtypeForUpdate?: string;
	headCellPos?: number;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	isComplete?: boolean;
	isNodeSelection?: boolean;
	isThinking?: boolean;
	isUpdate?: boolean;
	preserveSavedSelection?: boolean;
	reset?: boolean;
	thinkingSelectionFrom?: number;
	thinkingSelectionTo?: number;
};

export const remixThinkingPluginKey: PluginKey<RemixThinkingPluginState> =
	new PluginKey<RemixThinkingPluginState>('remixThinkingPlugin');

function getInitialState(): RemixThinkingPluginState {
	return {
		isThinking: false,
		isComplete: false,
		isUpdate: false,
		historyEntryForUpdate: undefined,
		dynamicUiSubtypeForUpdate: undefined,
		decorationSet: DecorationSet.empty,
	};
}

/**
 * normal flow:
 * - reset when show modal
 * - isThinking: true
 * - isThinking: true; isComplete: true (after we have final network response)
 * - isThinking: false; isComplete: true; (after onCompleteAnimationFinished)
 * 	 - we can't reset isComplete when stop thinking because we need it to show the ready toast
 *   - if the ready toast is not shown, currently, we're not resetting isComplete at all
 *   - if this become a bigger issue later, we need to properly reset it
 * - optional: isThinking: false; isComplete: false; (after clicking ready toast)
 *
 * update flow:
 * - isThinking: true; isUpdate: true;
 * - rest is same as normal flow
 */
function reducePluginState({
	api,
	getIntl,
	isViewMode,
	nodeViewPortalProviderAPI,
	newState,
	meta,
	pluginState,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	getIntl: PMPluginFactoryParams['getIntl'];
	isViewMode: boolean;
	meta: RemixThinkingPluginMeta;
	newState: EditorState;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	pluginState: RemixThinkingPluginState;
}): RemixThinkingPluginState | undefined {
	if (meta.reset) {
		return getInitialState();
	}

	if (
		meta.isThinking === true &&
		meta.thinkingSelectionFrom !== undefined &&
		meta.thinkingSelectionTo !== undefined
	) {
		const from = meta.thinkingSelectionFrom;
		const to = meta.thinkingSelectionTo;
		let decorations: Decoration[];

		if (meta.cellSelectionRanges) {
			decorations = meta.cellSelectionRanges.map((range) =>
				Decoration.inline(range.from, range.to, { class: THINKING_HIGHLIGHT_CLASS }),
			);
		} else if (meta.isNodeSelection) {
			decorations = [Decoration.node(from, to, { class: THINKING_HIGHLIGHT_CLASS })];
		} else {
			decorations = [Decoration.inline(from, to, { class: THINKING_HIGHLIGHT_CLASS })];
		}

		if (!isViewMode && fg('platform_editor_ai_remix_in_editor_loading')) {
			decorations.push(
				createRemixThinkingWidgetDecoration({
					api,
					contentType: meta.contentType,
					doc: newState.doc,
					getIntl,
					isComplete: false,
					isPlaceholder: fg('platform_editor_ai_remix_toolbar') ? meta.isUpdate ?? false : false,
					nodeViewPortalProviderAPI,
					to,
				}),
			);
		}

		const decorationSet = DecorationSet.create(newState.doc, decorations);
		const result: RemixThinkingPluginState = {
			contentType: fg('platform_editor_ai_remix_in_editor_loading') ? meta.contentType : undefined,
			isThinking: true,
			isUpdate: fg('platform_editor_ai_remix_toolbar') ? meta.isUpdate : undefined,
			dynamicUiSubtypeForUpdate: fg('platform_editor_ai_remix_toolbar')
				? meta.dynamicUiSubtypeForUpdate
				: undefined,
			historyEntryForUpdate: fg('platform_editor_ai_remix_toolbar')
				? meta.historyEntryForUpdate
				: undefined,
			isComplete: fg('platform_editor_ai_remix_in_editor_loading') ? false : undefined,
			decorationSet,
			savedSelection: {
				from,
				to,
				isNodeSelection: meta.isNodeSelection ?? false,
				anchorCellPos: meta.anchorCellPos,
				headCellPos: meta.headCellPos,
			},
		};
		return result;
	}

	if (meta.clearDecoration && fg('platform_editor_ai_remix_in_editor_loading')) {
		if (!pluginState.isThinking) {
			return pluginState;
		}

		const result: RemixThinkingPluginState = {
			contentType: pluginState.contentType,
			isThinking: true,
			isUpdate: pluginState.isUpdate,
			isComplete: false,
			historyEntryForUpdate: pluginState.historyEntryForUpdate,
			dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
			decorationSet: DecorationSet.empty,
			savedSelection: pluginState.savedSelection,
		};
		return result;
	}

	if (meta.isComplete === true && fg('platform_editor_ai_remix_in_editor_loading')) {
		if (pluginState.isComplete || !pluginState.isThinking) {
			return pluginState;
		}

		const result: RemixThinkingPluginState = {
			contentType: pluginState.contentType,
			isThinking: pluginState.isThinking,
			isUpdate: pluginState.isUpdate,
			isComplete: true,
			historyEntryForUpdate: pluginState.historyEntryForUpdate,
			dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
			decorationSet: pluginState.decorationSet,
			savedSelection: pluginState.savedSelection,
		};
		return result;
	}

	if (meta.isThinking === false) {
		const decorations: Decoration[] = [];
		if (!isViewMode && pluginState.isComplete && pluginState.savedSelection?.to) {
			decorations.push(
				createRemixThinkingWidgetDecoration({
					api,
					contentType: meta.contentType,
					doc: newState.doc,
					getIntl,
					isComplete: false,
					isPlaceholder: true,
					nodeViewPortalProviderAPI,
					to: pluginState.savedSelection.to,
				}),
			);
		}
		const result: RemixThinkingPluginState = {
			contentType: fg('platform_editor_ai_remix_in_editor_loading')
				? pluginState.contentType
				: undefined,
			isThinking: false,
			isUpdate: pluginState.isUpdate,
			dynamicUiSubtypeForUpdate: pluginState.dynamicUiSubtypeForUpdate,
			historyEntryForUpdate: pluginState.historyEntryForUpdate,
			isComplete: fg('platform_editor_ai_remix_in_editor_loading')
				? pluginState.isComplete
				: undefined,
			decorationSet: fg('platform_editor_ai_remix_in_editor_loading')
				? DecorationSet.create(newState.doc, decorations)
				: DecorationSet.empty,
			savedSelection: meta.preserveSavedSelection ? pluginState.savedSelection : undefined,
		};
		return result;
	}

	// this should be replaced by the reset state
	if (meta.isComplete === false && fg('platform_editor_ai_remix_in_editor_loading')) {
		if (pluginState.isComplete === false) {
			return pluginState;
		}

		const result: RemixThinkingPluginState = {
			contentType: undefined,
			isThinking: false,
			isComplete: false,
			isUpdate: false,
			historyEntryForUpdate: undefined,
			dynamicUiSubtypeForUpdate: undefined,
			decorationSet: DecorationSet.empty,
			savedSelection: undefined,
		};
		return result;
	}
}

export function createRemixThinkingPmPlugin({
	api,
	config,
	dispatch,
	getIntl,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
	dispatch: Dispatch;
	getIntl: PMPluginFactoryParams['getIntl'];
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin {
	return new SafePlugin({
		key: remixThinkingPluginKey,
		state: {
			init(): RemixThinkingPluginState {
				return getInitialState();
			},
			apply(tr, pluginState, _oldState, newState): RemixThinkingPluginState {
				const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
				const meta: RemixThinkingPluginMeta | undefined = tr.getMeta(remixThinkingPluginKey);

				if (meta) {
					const result = reducePluginState({
						api,
						getIntl,
						isViewMode,
						nodeViewPortalProviderAPI,
						newState,
						meta,
						pluginState,
					});
					if (result) {
						dispatch(remixThinkingPluginKey, result);
						return result;
					}
				}

				if (pluginState.isThinking && pluginState.decorationSet !== DecorationSet.empty) {
					let mappedDecorationSet = pluginState.decorationSet.map(tr.mapping, newState.doc);

					// re-create the thinking widget decoration if the widget is not in the mapped decoration set while thinking
					// it can be removed when updating the remix, which will replace the node and delete the decoration
					if (!isViewMode && fg('platform_editor_ai_remix_toolbar')) {
						const thinkingWidgetDecoration = mappedDecorationSet.find(
							undefined,
							undefined,
							(spec) => {
								return spec?.key === THINKING_WIDGET_KEY;
							},
						);
						if (!thinkingWidgetDecoration.length && pluginState.savedSelection?.to) {
							mappedDecorationSet = mappedDecorationSet.add(newState.doc, [
								createRemixThinkingWidgetDecoration({
									api,
									contentType: pluginState.contentType,
									doc: newState.doc,
									getIntl,
									isComplete: false,
									isPlaceholder: fg('platform_editor_ai_remix_toolbar')
										? pluginState.isUpdate ?? false
										: false,
									nodeViewPortalProviderAPI,
									to: tr.mapping.map(pluginState.savedSelection.to),
								}),
							]);
						}
					}

					if (mappedDecorationSet !== pluginState.decorationSet) {
						const mappedSavedSelection = pluginState.savedSelection
							? {
									...pluginState.savedSelection,
									from: tr.mapping.map(pluginState.savedSelection.from),
									to: tr.mapping.map(pluginState.savedSelection.to),
									anchorCellPos:
										pluginState.savedSelection.anchorCellPos !== undefined
											? tr.mapping.map(pluginState.savedSelection.anchorCellPos)
											: undefined,
									headCellPos:
										pluginState.savedSelection.headCellPos !== undefined
											? tr.mapping.map(pluginState.savedSelection.headCellPos)
											: undefined,
							  }
							: undefined;
						const result: RemixThinkingPluginState = {
							...pluginState,
							decorationSet: mappedDecorationSet,
							savedSelection: mappedSavedSelection,
						};
						dispatch(remixThinkingPluginKey, result);
						return result;
					}
				}

				return pluginState;
			},
		},

		view() {
			return {
				update(view, previousState) {
					const previousPluginState = remixThinkingPluginKey.getState(previousState);
					const currentPluginState = remixThinkingPluginKey.getState(view.state);

					const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
					if (
						!isViewMode &&
						currentPluginState?.isThinking &&
						!previousPluginState?.isComplete &&
						currentPluginState.isComplete &&
						(fg('platform_editor_ai_remix_toolbar') ? !currentPluginState.isUpdate : true) &&
						fg('platform_editor_ai_remix_in_editor_loading')
					) {
						const container = getRemixThinkingWidgetContainer(view);
						if (!container) {
							return;
						}

						renderRemixThinkingPortal({
							api,
							container,
							contentType: currentPluginState.contentType,
							getIntl,
							isComplete: currentPluginState.isComplete ?? false,
							nodeViewPortalProviderAPI,
						});
					}
				},
			};
		},

		appendTransaction(_transactions, oldState, newState) {
			const oldPluginState = remixThinkingPluginKey.getState(oldState);
			const newPluginState = remixThinkingPluginKey.getState(newState);

			// only restore selection if the plugin state is not complete
			// which means the remix is cancelled
			// we don't want to restore selection if the remix is complete
			// which will trigger floating toolbar
			if (
				oldPluginState?.isThinking &&
				!newPluginState?.isThinking &&
				newPluginState?.savedSelection &&
				!(newPluginState?.isComplete && fg('platform_editor_ai_remix_in_editor_loading'))
			) {
				const { savedSelection } = newPluginState;
				const tr = newState.tr;

				if (
					savedSelection.anchorCellPos !== undefined &&
					savedSelection.headCellPos !== undefined
				) {
					tr.setSelection(
						CellSelection.create(tr.doc, savedSelection.anchorCellPos, savedSelection.headCellPos),
					);
				} else if (savedSelection.isNodeSelection) {
					tr.setSelection(NodeSelection.create(tr.doc, savedSelection.from));
				} else {
					tr.setSelection(TextSelection.create(tr.doc, savedSelection.from, savedSelection.to));
				}

				return tr;
			}

			return null;
		},
		props: {
			decorations(state) {
				return remixThinkingPluginKey.getState(state)?.decorationSet ?? DecorationSet.empty;
			},
		},
	});
}
